import { deriveProductSemanticTags } from '@/lib/menuSemanticTags';
import { orderWithSuggestedFirst } from '@/lib/assistantProductFlow';
import {
  productIncludedHasDrink,
  productIncludedHasFries,
  productIsBurgerish,
  productIsFriesSideCandidate,
  productLooksLikeDrinkItem,
  productSuggestsExtraProteinAddon,
} from '@/lib/productComposition';

function idKey(x) {
  return x == null || x === '' ? '' : String(x);
}

/**
 * Sugerencias cross-sell coherentes con el asistente (misma heurística base).
 *
 * @param {object|null|undefined} addedProduct
 * @param {object} opts
 * @param {object|null|undefined} opts.menuData — `{ business, products, promos? }`
 * @param {any[]} [opts.cartLines] — líneas del carrito (`{ product }` como en CartContext)
 * @param {number} [opts.limit=3] — máximo de productos a devolver
 * @returns {object[]}
 */
export function pickCrossSellProducts(addedProduct, { menuData, cartLines = [], limit = 3 } = {}) {
  const cap = Math.max(1, Math.min(12, Number(limit) || 3));
  const business = menuData?.business || {};
  const allVisible = (menuData?.products || []).filter((p) => p && p.availability !== 'hidden');
  const suggestedIds = business?.suggested_product_ids || [];
  const lines = Array.isArray(cartLines) ? cartLines : [];
  const cartIds = new Set((lines || []).map((i) => idKey(i.product?.id)).filter(Boolean));
  const addKey = idKey(addedProduct?.id);
  if (addKey) cartIds.add(addKey);

  const promos = (menuData?.promos || []).filter((p) => p.is_active !== false);
  const addedTags = deriveProductSemanticTags(addedProduct, menuData?.business, promos);
  const addedHasFries = productIncludedHasFries(addedProduct) || addedTags.has_fries;
  const addedHasDrink = productIncludedHasDrink(addedProduct) || addedTags.has_drink;
  const addedIsBurger = productIsBurgerish(addedProduct) || addedTags.family === 'hamburguesa';

  const exclude = (p) => {
    const pk = idKey(p.id);
    if (!pk || cartIds.has(pk)) return true;
    if (addKey && pk === addKey) return true;
    if (addedHasFries && productIsFriesSideCandidate(p)) return true;
    return false;
  };

  const scoreCandidate = (p) => {
    if (exclude(p)) return -1;
    let s = 0;
    if (productLooksLikeDrinkItem(p)) s += addedHasDrink ? 10 : 22;
    if (productSuggestsExtraProteinAddon(p)) s += 9;
    if (productLooksLikeDrinkItem(p) && addedIsBurger && addedHasFries) s += 4;
    if (productSuggestsExtraProteinAddon(p) && addedIsBurger) s += 3;
    const idx = suggestedIds.findIndex((id) => idKey(id) === idKey(p.id));
    if (idx >= 0) s += 6 - Math.min(3, idx);
    const sale = Number(p.sales_count) || 0;
    s += Math.min(4, sale / 10);
    if (productIsFriesSideCandidate(p) && !addedHasFries) s += 3;
    return s;
  };

  const picked = [];
  for (const id of suggestedIds) {
    if (picked.length >= cap) break;
    const sid = idKey(id);
    if (!sid || cartIds.has(sid)) continue;
    const p = allVisible.find((x) => idKey(x.id) === sid);
    if (p && !exclude(p)) picked.push(p);
  }

  if (picked.length < cap) {
    const scored = allVisible
      .map((p) => ({ p, s: scoreCandidate(p) }))
      .filter((x) => x.s >= 0)
      .sort((a, b) => b.s - a.s);
    for (const { p } of scored) {
      if (picked.length >= cap) break;
      if (picked.some((x) => idKey(x.id) === idKey(p.id))) continue;
      picked.push(p);
    }
  }

  if (picked.length < cap) {
    const ordered = orderWithSuggestedFirst(allVisible, business);
    for (const p of ordered) {
      if (picked.length >= cap) break;
      const pk = idKey(p.id);
      if (!pk || exclude(p)) continue;
      if (picked.some((x) => idKey(x.id) === pk)) continue;
      picked.push(p);
    }
  }

  if (!addedHasDrink && picked.length > 1) {
    picked.sort((a, b) => {
      const da = productLooksLikeDrinkItem(a) ? 1 : 0;
      const db = productLooksLikeDrinkItem(b) ? 1 : 0;
      if (db !== da) return db - da;
      return 0;
    });
  }

  return picked;
}
